import { useMemo } from 'react';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { useFormBuilderStore } from '@/features/form-builder/store/form-builder.store';
import { FormField, FormPage as Page, FormSection as Section } from '@/features/application/types/forms';
import { CrmMappingInput } from './CrmMappingInput';
import { useCrmMapping } from '../hooks/use-crm-mapping.hook';

interface MappingEditorPaneProps {
  className?: string;
}

// Purpose: Right-pane focused editor for the currently selected field
const MappingEditorPane = ({ className = '' }: MappingEditorPaneProps) => {
  const { currentMappingStructure, activeFieldId } = useFormBuilderStore();

  const activeField: FormField | null = useMemo(() => {
    if (!currentMappingStructure || !activeFieldId) return null;
    for (const page of currentMappingStructure.pages as Page[]) {
      for (const section of page.sections as Section[]) {
        const found = section.fields.find((f) => f.id === activeFieldId);
        if (found) return found;
      }
    }
    return null;
  }, [currentMappingStructure, activeFieldId]);

  return (
    <section className={`flex-1 p-6 ${className}`}>
      <div className="max-w-4xl mx-auto">
        {!activeField ? (
          <div className="border rounded-md bg-white p-6 text-gray-600">
            Select a field from the Form Structure to edit its CRM mapping.
          </div>
        ) : (
          <MappingEditorContent field={activeField} />
        )}
      </div>
    </section>
  );
};

export default MappingEditorPane;

// Child component to keep hooks order stable; rendered only when a field is selected
const MappingEditorContent = ({ field }: { field: FormField }) => {
  const { currentMappingStructure, activeFieldId, setActiveFieldId } = useFormBuilderStore();

  const allFields: FormField[] = useMemo(() => {
    if (!currentMappingStructure) return [] as FormField[];
    const acc: FormField[] = [];
    for (const page of currentMappingStructure.pages as Page[]) {
      for (const section of page.sections as Section[]) {
        acc.push(...section.fields);
      }
    }
    return acc;
  }, [currentMappingStructure]);

  const currentIndex = useMemo(() => {
    if (!activeFieldId) return -1;
    return allFields.findIndex((f) => f.id === activeFieldId);
  }, [allFields, activeFieldId]);

  const goPrev = () => {
    if (currentIndex > 0) {
      setActiveFieldId(allFields[currentIndex - 1].id);
    }
  };

  const goNext = () => {
    if (currentIndex > -1 && currentIndex < allFields.length - 1) {
      setActiveFieldId(allFields[currentIndex + 1].id);
    }
  };
  const { isMapped: _isMapped, getMappingSummary, ...crmMappingProps } = useCrmMapping(
    field,
    undefined,
    true
  );
  return (
    <div>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-800">{field.label}</h3>
        <p className="text-sm text-gray-500">{getMappingSummary()}</p>
      </div>
      <div className="bg-white border rounded-md">
        <CrmMappingInput key={field.fieldApiName || field.id} field={field} {...crmMappingProps} />
      </div>
      <div className="mt-3 flex items-center justify-between gap-2">
        <button
          type="button"
          onClick={goPrev}
          disabled={currentIndex <= 0}
          className="px-3 py-1 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          aria-label="Previous field"
        >
          <ChevronLeft className="h-4 w-4" />
          <span>Prev</span>
        </button>
        <button
          type="button"
          onClick={goNext}
          disabled={currentIndex === -1 || currentIndex >= allFields.length - 1}
          className="px-3 py-1 text-sm border rounded-md bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-1"
          aria-label="Next field"
        >
          <span>Next</span>
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
};


